'use client';

import { Fragment, useState } from 'react';
import Link from 'next/link';
import styles from './NicheExpertise.module.scss';
import { sectors, sectorSlugs } from '@/src/data/sectors';
import { gaClick, gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface NicheItem {
  slug?: string;
  title: string;
  description: string;
  icon?: string;
  cardImage?: string;
  teaser?: string;
  href?: string;
}

interface NicheExpertiseProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  items?: NicheItem[];
  lang?: string;
  id?: string;
  variant?: 'default' | 'wide-staggered';
  beyondTitle?: string;
  beyondText?: string;
  beyondLinkLabel?: string;
  beyondLinkHref?: string;
}

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

const buildDefaultItems = (lang: string): NicheItem[] =>
  sectorSlugs.map((slug) => {
    const s = sectors[slug];
    const l = (lang === 'fr' ? 'fr' : 'en') as 'fr' | 'en';
    return {
      slug,
      title: s.title[l],
      description: s.cardDescription?.[l] || '',
      icon: s.icon || '•',
      cardImage: s.cardImage,
      teaser: s.teaser?.[l],
    };
  });

export function NicheExpertise({
  eyebrow = 'EXPERTISES DE NICHE',
  title = 'Nos secteurs',
  highlightedTitle = '*de prédilection*',
  description = 'Quatre domaines où nous avons déjà livré des produits mesurables, de l’organisme de formation à la supply chain.',
  items,
  lang = 'fr',
  id,
  variant = 'default',
  beyondTitle,
  beyondText,
  beyondLinkLabel,
  beyondLinkHref,
}: NicheExpertiseProps) {
  useAOS();

  const resolvedItems = items && items.length > 0 ? items : buildDefaultItems(lang);
  const [active, setActive] = useState<number | null>(null);

  const defaultBeyondTitle = lang === 'fr' ? 'Et au-delà' : 'Beyond these sectors';
  const defaultBeyondText =
    lang === 'fr'
      ? 'Nous intervenons aussi sur d’autres sujets — retail, fintech, industrie, services — en apportant notre rigueur de delivery et notre capacité à monter rapidement en compétence avec vos équipes métiers.'
      : 'We also work on other topics — retail, fintech, manufacturing, services — bringing the same delivery discipline and our ability to ramp up quickly with your business teams.';
  const defaultBeyondLinkLabel = lang === 'fr' ? 'Voir toutes nos expertises' : 'See all expertise';
  const defaultBeyondLinkHref = '/#niches';

  return (
    <section
      id={id}
      className={`${styles.root} ${variant === 'wide-staggered' ? styles.wideStaggered : ''}`}
    >
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)} {renderTitle(highlightedTitle)}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>

        <div
          className={styles.accordion}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
          role="list"
        >
          {resolvedItems.map((item, index) => {
            const href =
              item.href || (item.slug ? `/${lang}/expertise/${item.slug}` : `/${lang}/expertise`);
            const isActive = active === index;
            return (
              <Link
                key={`${item.slug || item.title}-${index}`}
                href={href}
                role="listitem"
                className={`${styles.panel} ${isActive ? styles.panelActive : ''}`}
                onMouseEnter={() => setActive(index)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(index)}
                onBlur={() => setActive(null)}
                onClick={() => gaClick('niche_expertise_card', { niche_title: item.title, href })}
              >
                {item.cardImage && (
                  <>
                    <img src={item.cardImage} alt="" className={styles.panelBg} loading="lazy" />
                    <div className={styles.panelOverlay} aria-hidden="true" />
                  </>
                )}
                <div className={styles.panelContent}>
                  <div className={styles.panelText}>
                    <p className={styles.panelDesc}>{item.description}</p>
                    <span className={styles.panelLink}>
                      {lang === 'fr' ? 'Explorer' : 'Explore'}
                      <span aria-hidden="true">→</span>
                    </span>
                  </div>
                  <div className={styles.panelBottom}>
                    {item.teaser && <span className={styles.panelTeaser}>{item.teaser}</span>}
                    <h3 className={styles.panelTitle}>{item.title}</h3>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div
          className={styles.beyond}
          data-aos="fade-up"
          data-aos-duration="700"
          data-aos-delay="300"
        >
          <p className={styles.beyondTitle}>{beyondTitle || defaultBeyondTitle}</p>
          <p className={styles.beyondText}>{beyondText || defaultBeyondText}</p>
          {(beyondLinkHref || defaultBeyondLinkHref) && (
            <Link
              href={beyondLinkHref || defaultBeyondLinkHref}
              className={styles.beyondLink}
              onClick={() =>
                gaCta(
                  beyondLinkLabel || defaultBeyondLinkLabel,
                  'niche_expertise_cta',
                  beyondLinkHref || defaultBeyondLinkHref
                )
              }
            >
              {beyondLinkLabel || defaultBeyondLinkLabel}
              <span aria-hidden="true">→</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
